import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent } from '@medikei/ui';
import { Search, FileText, Calendar, ChevronLeft, ChevronRight, Phone, Mail, Pill } from 'lucide-react';
import { doctorsApi } from '@/api';
import { LoadingOverlay } from '@/components/ui/loading-overlay';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorRetry } from '@/components/ui/ErrorRetry';

interface Patient {
  id: string;
  name: string;
  email: string;
  phone?: string;
  cpf: string;
  birthDate: string;
  gender?: string;
  totalConsultations: number;
  lastConsultation?: string;
  healthConditions?: string[];
  allergies?: string[];
}

interface PatientsResponse {
  patients: Patient[];
  total: number;
  page: number;
  pageSize: number;
}

export function DoctorPatientsPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const { data, isLoading, isError, refetch } = useQuery<PatientsResponse>({
    queryKey: ['doctor', 'patients', page],
    queryFn: async () => {
      const response = await doctorsApi.getMyPatients(page);
      return {
        patients: response.patients,
        total: response.total,
        page: response.page,
        pageSize: response.pageSize,
      };
    },
  });

  const filteredPatients = data?.patients.filter((p) => {
    const matchesSearch = p.name.toLowerCase().includes(search.toLowerCase()) ||
                          p.email.toLowerCase().includes(search.toLowerCase()) ||
                          p.cpf.includes(search);
    return matchesSearch;
  }) || [];

  const formatDate = (dateStr: string) => {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(new Date(dateStr));
  };

  const calculateAge = (birthDate: string) => {
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Meus Pacientes</h1>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="text"
          placeholder="Buscar por nome, email ou CPF..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {isLoading ? (
        <LoadingOverlay message="Carregando pacientes..." />
      ) : isError ? (
        <ErrorRetry message="Erro ao carregar pacientes" onRetry={() => refetch()} />
      ) : filteredPatients.length === 0 ? (
        <EmptyState
          icon="User"
          title="Nenhum paciente encontrado"
          description={search ? 'Tente ajustar a busca' : 'Você ainda não tem pacientes'}
          action={{ label: 'Atualizar', onClick: () => refetch() }}
        />
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredPatients.map((patient) => (
              <Card key={patient.id}>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center text-lg font-bold text-blue-600">
                      {patient.name.charAt(0)}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-lg">{patient.name}</h3>
                      <p className="text-sm text-gray-500">{patient.gender || 'Não informado'} • {calculateAge(patient.birthDate)} anos</p>
                    </div>
                  </div>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2 text-sm">
                      <Mail className="h-4 w-4 text-gray-400" />
                      <span className="text-gray-600">{patient.email}</span>
                    </div>
                    {patient.phone && (
                      <div className="flex items-center gap-2 text-sm">
                        <Phone className="h-4 w-4 text-gray-400" />
                        <span className="text-gray-600">{patient.phone}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar className="h-4 w-4 text-gray-400" />
                      <span className="text-gray-600">
                        {patient.totalConsultations} consultas • Última: {patient.lastConsultation ? formatDate(patient.lastConsultation) : 'Nenhuma'}
                      </span>
                    </div>
                  </div>

                  {patient.healthConditions && patient.healthConditions.length > 0 && (
                    <div className="mb-3">
                      <p className="text-xs font-medium text-gray-500 mb-1">Condições de Saúde</p>
                      <div className="flex flex-wrap gap-1">
                        {patient.healthConditions.map((condition, idx) => (
                          <span key={idx} className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded-full">
                            {condition}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {patient.allergies && patient.allergies.length > 0 && (
                    <div className="mb-3">
                      <p className="text-xs font-medium text-gray-500 mb-1">Alergias</p>
                      <div className="flex flex-wrap gap-1">
                        {patient.allergies.map((allergy, idx) => (
                          <span key={idx} className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full">
                            {allergy}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                    <div className="flex items-center gap-2 pt-3 border-t">
                      <button
                        onClick={() => navigate(`/medico/pacientes/${patient.id}/prontuarios`)}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 transition-colors shadow-xs"
                      >
                        <FileText className="h-4 w-4" />
                        Ver Prontuário
                      </button>
                      <button
                        onClick={() => navigate(`/medico/receitas/novo?patientId=${patient.id}`)}
                        className="p-2 border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300 rounded-xl hover:bg-blue-100 transition-colors"
                        title="Prescrever via MeMed (Receita Digital ICP-Brasil)"
                        aria-label="Prescrever via MeMed"
                      >
                        <Pill className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                      </button>
                    </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="flex items-center justify-center gap-2">
            <button
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
              className="p-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="px-4 py-2 bg-blue-600 text-white rounded-lg">{page}</span>
            <button
              onClick={() => setPage(page + 1)}
              disabled={page * 10 >= (data?.total || 0)}
              className="p-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </>
      )}
    </div>
  );
}
